import { useEffect, useMemo } from 'react';

// HOOKs
import { useNotificationContext } from 'contexts/NotificationContext';
import { useInfiniteNotificaiton } from 'services/notifications';
import { useToggle } from 'hooks';

// COMPs
import {
  Button,
  ButtonEmpty,
  RadioGroup,
  FlexGroup,
  FlexItem,
  LoadingSpinner,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalHeaderTitle,
  Outline,
  Spacer,
  Text,
} from '@antoree/ant-ui';
import NotificationBody from './NotificationBody';
import NotificationHeader from './NotificationHeader';

import { useRead } from './hooks';
import styles from './Notification.module.scss';
import { isTimeMoreThan3 } from './utils';
import ReScheduleModal from './components/ReScheduleModal';

type Props = {
  closePopover: () => void;
  isPopoverOpen: boolean;
};

const NotificationPopover = ({ closePopover, isPopoverOpen }: Props) => {
  const { handleClickAll, handleClickUnread, isRead } = useRead();

  const { notification: notificationData } = useNotificationContext();
  const {
    data,
    isLoading,
    hasNextPage,
    refetch,
    fetchNextPage,
  } = useInfiniteNotificaiton(
    {
      pageSize: 10,
      isRead,
    },
    {
      enabled: isPopoverOpen,
      cacheTime: 0,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
    },
  );

  const dataLength = useMemo(
    () =>
      data?.pages.reduce((acc, page) => {
        return acc + page.data.notifications.length;
      }, 0) ?? 0,
    [data],
  );

  const notifications = data?.pages
    ?.map(page => page.data?.notifications)
    .reduce((a, b) => [...a, ...b], [])
    .map(item => {
      const isBefore = isTimeMoreThan3(item?.createdAt);
      return { ...item, isBefore };
    });

  useEffect(() => {
    setTimeout(() => refetch(), 500);
  }, [notificationData]);

  if (isLoading) {
    return (
      <>
        <div className={styles.notificationPopoverContainer}>
          <div className="flex flex-col items-center justify-center justify-items-center py-10">
            <LoadingSpinner />
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className={styles.notificationPopoverContainer}>
        {notifications && (
          <NotificationHeader
            handleClickUnread={handleClickUnread}
            handleClickAll={handleClickAll}
            isUnread={!isRead}
          />
        )}
        <Spacer />
        {notifications && (
          <NotificationBody
            closePopover={closePopover}
            height={500}
            notifications={notifications}
            dataLength={dataLength}
            fetchNextPage={fetchNextPage}
            hasNextPage={hasNextPage}
          />
        )}
      </div>
    </>
  );
};

export default NotificationPopover;
